import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, ShieldCheck, TriangleAlert, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export const AuditDialog = ({ onClose, onChanged }) => {
  const [scope, setScope] = useState("published");
  const [data, setData] = useState(null);
  const [fixing, setFixing] = useState(null);
  const [done, setDone] = useState({});

  const load = async (s = scope) => {
    setData(null);
    try { setData((await api.get("/projects/audit/origin", { params: { scope: s } })).data); } catch (e) { toast.error(errMsg(e)); onClose(); }
  };
  useEffect(() => { load(scope); /* eslint-disable-next-line */ }, [scope]);

  // Mirror the assets and republish, otherwise visitors keep getting the old snapshot.
  const fix = async (p) => {
    setFixing(p.id);
    try {
      let last = null, rounds = 0, files = 0, independent = false, failed = 0;
      do {
        const { data } = await api.post(`/projects/${p.id}/mirror`, { apply: true });
        files += data.mirrored;
        independent = data.independent;
        failed = data.remaining ?? data.failed;
        const progressed = last === null || data.remaining < last;
        last = data.remaining;
        rounds++;
        if (!data.remaining || !progressed || rounds >= 5) break;
      } while (true);
      if (p.status === "published") await api.post(`/projects/${p.id}/publish`);
      setDone((d) => ({ ...d, [p.id]: independent ? "ok" : "partial" }));
      toast[independent ? "success" : "warning"](`${p.name}: ${files} arquivo(s) espelhado(s)${independent ? " — livre da origem" : `, ${failed} ainda de fora`}`);
      onChanged?.();
    } catch (e) { toast.error(`${p.name}: ${errMsg(e)}`); setDone((d) => ({ ...d, [p.id]: "fail" })); } finally { setFixing(null); }
  };

  const fixAll = async () => { for (const p of data.dependents) if (!done[p.id]) await fix(p); };
  const pending = data ? data.dependents.filter((p) => done[p.id] !== "ok") : [];

  return (
    <div className="fixed inset-0 z-50 bg-[var(--ink)]/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => !fixing && onClose()}>
      <div data-testid="audit-dialog" onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white rounded-3xl border border-slate-200 shadow-2xl p-6 max-h-[88vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display font-bold text-xl text-[var(--ink)]">Dependência de outros sites</h3>
            <p className="text-sm text-slate-500 mt-1">Conferimos o que o visitante realmente carrega — nas páginas publicadas olhamos a versão publicada.</p>
          </div>
          <button data-testid="audit-close" onClick={onClose} className="p-2 -mr-2 -mt-1 text-slate-400 hover:text-[var(--ink)]"><X size={18} /></button>
        </div>

        <div className="mt-4 flex rounded-xl border border-slate-200 p-1 text-sm w-fit">
          {[["published", "Publicadas"], ["all", "Todas"]].map(([v, l]) => (
            <button key={v} data-testid={`audit-scope-${v}`} onClick={() => setScope(v)} className={`px-3 h-8 rounded-lg ${scope === v ? "bg-[var(--ink)] text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>

        {!data ? <div className="py-12 grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div> : (
          <>
            <div data-testid="audit-summary" className="mt-4 rounded-2xl border border-slate-200 p-4 flex items-center gap-6">
              <div><div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">verificadas</div><div className="font-display font-bold text-lg">{data.checked}</div></div>
              <div><div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">dependem de fora</div><div data-testid="audit-dependents-count" className={`font-display font-bold text-lg ${pending.length ? "text-amber-600" : "text-emerald-600"}`}>{pending.length}</div></div>
              {pending.length > 1 && <button data-testid="audit-fix-all" disabled={!!fixing} onClick={fixAll} className="btn btn-primary !py-2 !px-3 text-xs ml-auto disabled:opacity-60">{fixing ? "Corrigindo…" : "Corrigir todas"}</button>}
            </div>

            {!data.checked && <p data-testid="audit-empty" className="mt-5 text-sm text-slate-500">Você ainda não tem páginas {scope === "published" ? "publicadas" : "nesta conta"}.</p>}
            {!!data.checked && !data.dependents.length && (
              <div data-testid="audit-all-clear" className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-2 text-sm text-emerald-700"><ShieldCheck size={16} /> Nenhuma página depende de arquivos de outro site.</div>
            )}

            <div className="mt-4 space-y-2">
              {data.dependents.map((p) => (
                <div key={p.id} data-testid={`audit-item-${p.id}`} className="rounded-2xl border border-slate-200 p-3 flex items-center gap-3">
                  <TriangleAlert size={15} className={done[p.id] === "ok" ? "text-emerald-600 shrink-0" : "text-amber-600 shrink-0"} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[var(--ink)] truncate">{p.name} <span className="font-mono text-[11px] text-slate-400">/{p.slug}</span></div>
                    <div className="text-xs text-slate-500 truncate">{done[p.id] === "ok" ? "corrigida e republicada" : `${p.external_assets} arquivo(s) de ${p.external_hosts.join(", ") || "outro site"}`}{p.stale && done[p.id] !== "ok" ? " · há alterações não publicadas" : ""}</div>
                  </div>
                  {done[p.id] === "ok"
                    ? <span data-testid={`audit-fixed-${p.id}`} className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium shrink-0"><Check size={13} /> ok</span>
                    : <button data-testid={`audit-fix-${p.id}`} disabled={!!fixing} onClick={() => fix(p)} className="btn btn-ghost !py-1.5 !px-3 text-xs shrink-0 disabled:opacity-60">{fixing === p.id ? <><Loader2 size={12} className="animate-spin" /> corrigindo…</> : "Corrigir"}</button>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
